import * as React from 'react';
import { ISpPageContext } from '@ms/odsp-datasources/lib/interfaces/ISpPageContext';
import { IPeoplePickerDataSource, IPeoplePickerQueryParams, IPerson, IPeoplePickerProvider } from '@ms/odsp-datasources/lib/PeoplePicker';
import { IPickerItemProps } from 'office-ui-fabric-react/lib/Pickers';
import { IBaseProps } from 'office-ui-fabric-react/lib/Utilities';

export enum PeoplePickerType {
  // The standard PeoplePicker with items rendered inline.
  normal,
  // Works the same as the Standard PeoplePicker but items are rendered the picker search box.
  listBelow
}

export interface IPeoplePickerProps extends React.HTMLAttributes<HTMLElement>, IBaseProps {
  /**
   * Query parameters for the dataSource search.
   */
  peoplePickerQueryParams?: IPeoplePickerQueryParams;
  /**
   * Already selected items.
   */
  defaultSelectedItems?: IPerson[];
  /**
   * Controlled items list.
   */
  selectedItems?: IPerson[];
  /**
   * The context of the current page that the user is in.
   */
  context?: ISpPageContext;
  /**
   * The selected type of the people picker
   * @default PeoplePickerType.Normal
   */
  peoplePickerType?: PeoplePickerType;
  /**
   * How a selected item should be rendered.
   */
  onRenderItem?: (props: IPickerItemProps<IPerson>) => JSX.Element;
  /**
   * How an indivual suggestion item should be rendered.
   */
  onRenderSuggestionsItem?: (props: IPerson) => JSX.Element;
  /**
   * A variable that allows a custom dataSource to be used by the peoplePickerProvider.
   */
  dataSource?: IPeoplePickerDataSource;

  /**
   * A variable that allows a custom provider to be used for people picker API calls.
   */
  dataProvider?: IPeoplePickerProvider;
  /**
   * A callback for when the list of selected personas change.
   */
  onSelectedPersonasChange?: (items?: IPerson[]) => void;
  /**
   * ClassName for the picker.
   */
  className?: string;
  /**
   * The text that should appear at the top of the suggestion box.
   */
  suggestionsHeaderText?: string;
  /**
   * Class to apply to Suggestions component.
   */
  suggestionsClassName?: string;
  /**
   * the text that should appear when no results are returned.
   */
  noResultsFoundText?: string;
  /**
   * The text that should appear on the button to search for more.
   */
  searchForMoreText?: string;
  /**
   * The text that should appear while results are loading.
   */
  loadingText?: string;
  /**
   * Input element native props to be put onto the input element.
   */
  inputProps?: React.HTMLProps<HTMLInputElement>;
  /**
   * A callback for when the suggestions of personas are resolved.
   */
  onResolvedSuggestions?: (personas: IPerson[], query: string) => IPerson[] | PromiseLike<IPerson[]>;
  /**
   * A callback for when a suggestion is selected by the user.
   */
  onSuggestionSelected?: (suggestion: IPerson | undefined) => IPerson | PromiseLike<IPerson>;
  /**
   * Used to enable suggestions to read in Internet Explorer.
   */
  enableSelectedSuggestionAlert?: boolean;
  /**
   * The message that is read when suggestions become available.
   */
  suggestionsAvailableAlertText?: string;
  /**
   * The maximum number of people to select.
   */
  itemLimit?: number;
  /**
   * A callback used to modify the input string.
   */
  onInputChange?: (input: string) => string;
  /**
   * A callback for when the "Search Directory" button is clicked.
   */
  onGetMoreResults?: (query: string) => IPerson[] | PromiseLike<IPerson[]>;
  /**
   * A callback for when a persona is removed from the suggestion list
   */
  onRemoveSuggestion?: () => void;
}
